import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Compass, ClipboardCheck, ShieldCheck,
  Bell, FileSearch, GitBranch, AlertTriangle, Scale, Briefcase } from "lucide-react";

import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import Reveal from "@/components/site/Reveal";
import { TESTIDS } from "@/lib/testIds";
import { setPathway, withPathway } from "@/lib/pathway";

/**
 * /investors — public pathway page.
 *
 * ImpactVest for investors, family offices, funds and lenders.
 * Signature output: Decision Passport.
 * Journey: Review → Decide → Preserve → Revisit.
 *
 * Design contract: reuse the ImpactVest editorial system exactly
 * (ivory/forest/gold palette, serif display, monospace eyebrows,
 * no framer-motion, Reveal + CSS transitions only). Do not
 * introduce new visual primitives.
 */

const JOURNEY = [
  { n: "01", label: "Review",
    body: "Examine the opportunity in context. Connect every material claim to its supporting source; distinguish measured results, targets, projections and management assertions.",
    icon: Compass },
  { n: "02", label: "Decide",
    body: "Preserve the original investment thesis, the decisive evidence, unresolved questions, approval conditions, dissenting views and the exact approved version.",
    icon: ClipboardCheck },
  { n: "03", label: "Preserve",
    body: "Generate an evidence-linked Decision Passport with an HMAC-SHA256 signature. The reasoning behind the decision is carried forward — not just the documents.",
    icon: ShieldCheck },
  { n: "04", label: "Revisit",
    body: "Turn approval conditions into monitorable triggers. Recognise when material assumptions no longer hold and the decision deserves another look.",
    icon: Bell },
];

const PASSPORT_CONTENTS = [
  "Transaction and company context",
  "Original investment thesis",
  "Company claims mapped to supporting evidence",
  "Evidence-strength indicators per claim",
  "Contradictions and unresolved questions",
  "Financially material ESG factors",
  "KPI quality and comparability notes",
  "Missing information and diligence gaps",
  "Scenario and transition-plan dependencies",
  "What must remain true (approval conditions)",
  "Reconsideration triggers with named monitoring owners",
  "Decision history and version differences",
];

const CAPABILITIES = [
  { icon: FileSearch, t: "Source-linked provenance",
    b: "Every material claim is connected to its underlying source and location — not to a summary." },
  { icon: GitBranch, t: "Contradiction Register",
    b: "Side-by-side view of conflicting evidence across pitch materials, financials, KPI files and reports." },
  { icon: AlertTriangle, t: "Transition credibility gaps",
    b: "The assumptions the thesis depends on but the evidence does not yet support are surfaced explicitly." },
  { icon: Scale, t: "No composite score, no recommendation",
    b: "ImpactVest does not emit a buy/hold/pass verdict or a single ESG number. Judgement remains with the investment team." },
];

export default function InvestorsPathway() {
  useEffect(() => {
    document.title = "ImpactVest for Investors | Evaluate an opportunity";
    setPathway("investor");
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  return (
    <div className="relative" data-testid={TESTIDS.pathway.investorPage}>
      <Header />
      <main>
        <PathwayHero />
        <Journey />
        <PassportOutput />
        <Capabilities />
        <CtaBand />
      </main>
      <Footer />
    </div>
  );
}

function PathwayHero() {
  return (
    <section className="relative pt-28 md:pt-36 pb-20 md:pb-24 overflow-hidden iv-anchor" id="top">
      <div className="grain" aria-hidden />
      <div className="iv-container relative">
        <div className="grid grid-cols-12 gap-8 md:gap-12 items-start">
          <div className="col-span-12 lg:col-span-7">
            <Link to="/" className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-iv-fg-3 hover:text-iv-fg mb-6 inline-flex items-center gap-2">
              <span aria-hidden>←</span> ImpactVest
            </Link>
            <div className="iv-eyebrow mb-8 flex items-center gap-3">
              <Briefcase size={14} strokeWidth={1.7} />
              For investors · family offices · funds · lenders
            </div>
            <h1 className="font-serif-display text-[9vw] sm:text-[7vw] md:text-[4.6vw] lg:text-[3.6rem] xl:text-[4rem] leading-[1.02] tracking-[-0.02em] text-iv-fg">
              Evaluate the evidence<br className="hidden md:block" /> behind an investment.
            </h1>
            <p className="mt-7 text-[17px] md:text-[18px] leading-relaxed text-iv-fg-2 max-w-xl">
              ImpactVest helps investors examine the evidence behind a company&rsquo;s impact and ESG
              claims, identify material risks and contradictions, and preserve the reasoning behind an
              investment decision — so it can be revisited intelligently as conditions evolve.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Link
                to={withPathway("/sample", "investor")}
                data-testid={TESTIDS.pathway.investorPrimaryCta}
                className="iv-btn iv-btn--primary"
              >
                Try an investment decision <ArrowRight size={16} />
              </Link>
              <Link
                to={withPathway("/access", "investor")}
                data-testid={TESTIDS.pathway.investorSecondaryCta}
                className="iv-btn"
              >
                Analyse a live opportunity <ArrowUpRight size={16} />
              </Link>
            </div>
            <p className="mt-7 font-mono text-[11px] tracking-[0.14em] uppercase text-iv-fg-3">
              Signature output · Decision Passport · Evidence-linked · HMAC-signed
            </p>
          </div>

          <aside className="col-span-12 lg:col-span-5">
            <div className="iv-canvas rounded-[3px]">
              <div className="px-6 pt-5 pb-4 border-b border-[var(--ivc-line)] flex items-center justify-between">
                <span className="font-mono text-[11px] tracking-[0.28em] uppercase text-[var(--ivc-gold-2)]">Decision Passport</span>
                <span className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-[var(--ivc-ivory-3)]">Illustrative</span>
              </div>
              <div className="px-7 pt-7 pb-6">
                <div className="text-[11px] font-mono tracking-[0.22em] uppercase text-[var(--ivc-ivory-3)]">Opportunity</div>
                <div className="font-serif-display text-[24px] leading-tight mt-2 text-[var(--ivc-ivory)]">
                  Bridge Investment · Series B
                </div>
                <div className="mt-6 border-l-2 border-[var(--ivc-forest)] pl-5 py-1">
                  <div className="text-[10.5px] font-mono tracking-[0.22em] uppercase text-[var(--ivc-forest)]">Thesis</div>
                  <div className="mt-1.5 text-[14.5px] leading-snug text-[var(--ivc-ivory)]">
                    Contracted anchor revenue, verified emissions reduction, defensible unit economics.
                  </div>
                </div>
              </div>
              <div className="px-7 pb-7 border-t border-[var(--ivc-line-strong)] pt-5">
                <div className="text-[10.5px] font-mono tracking-[0.22em] uppercase text-[var(--ivc-ivory-3)] mb-3">Contradiction detected</div>
                <div className="text-[13.5px] leading-snug text-[var(--ivc-ivory-2)]">
                  Pitch deck reports 42% gross margin; Q3 financial model implies 31%. Reviewer assignment
                  required before release.
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}

function Journey() {
  return (
    <section className="relative py-24 md:py-28 border-t border-iv-line">
      <div className="iv-container">
        <div className="iv-eyebrow mb-10 md:mb-14">Review → Decide → Preserve → Revisit</div>
        <div className="border-t border-iv-line">
          {JOURNEY.map((s, i) => {
            const Icon = s.icon;
            return (
              <Reveal
                key={s.n}
                delay={i * 0.05}
                className="grid grid-cols-12 gap-4 md:gap-10 py-8 md:py-10 border-b border-iv-line items-baseline"
              >
                <div className="col-span-12 md:col-span-2 iv-chapter-number !text-[4rem] md:!text-[5rem]">{s.n}</div>
                <h3 className="col-span-12 md:col-span-3 font-serif-display text-[28px] md:text-[36px] leading-tight text-iv-fg flex items-center gap-3">
                  <span className="text-iv-green"><Icon size={22} strokeWidth={1.5} /></span>
                  {s.label}
                </h3>
                <p className="col-span-12 md:col-span-7 text-[15px] leading-relaxed text-iv-fg-2">{s.body}</p>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function PassportOutput() {
  return (
    <section className="iv-band-immersive relative py-28 md:py-36 overflow-hidden">
      <div className="iv-container relative">
        <div className="iv-canvas-eyebrow mb-10 md:mb-14">Signature output — Decision Passport</div>
        <div className="grid grid-cols-12 gap-8 md:gap-12 items-start">
          <div className="col-span-12 lg:col-span-6">
            <h2 className="font-serif-display text-[38px] md:text-[54px] leading-[1.02] tracking-tight text-[var(--ivc-ivory)]">
              Every investment gains a Decision Passport.
            </h2>
            <p className="mt-8 text-[16px] leading-relaxed text-[var(--ivc-ivory-2)] max-w-xl">
              A single evidence-linked record that carries forward the thesis, the decisive evidence,
              the material uncertainties and the reconsideration triggers behind an investment —
              cryptographically signed, versioned and diff-able.
            </p>
            <p className="mt-6 text-[14px] leading-relaxed text-[var(--ivc-ivory-3)] max-w-xl">
              ImpactVest never emits a buy/hold/pass recommendation and never reduces the decision to a
              composite score. Judgement remains with the investment team; ImpactVest preserves the
              evidence and the reasoning that supported it.
            </p>
          </div>
          <div className="col-span-12 lg:col-span-6">
            <div className="border-t border-[var(--ivc-line-strong)]">
              {PASSPORT_CONTENTS.map((c, i) => (
                <div key={c} className="grid grid-cols-[52px_1fr] gap-4 items-baseline py-3.5 border-b border-[var(--ivc-line)]">
                  <span className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-[var(--ivc-gold-2)]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-serif-display text-[17px] leading-snug text-[var(--ivc-ivory)]">{c}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Capabilities() {
  return (
    <section className="relative py-24 md:py-28 border-t border-iv-line">
      <div className="iv-container">
        <div className="iv-eyebrow mb-10">Evidence integrity built into every Passport</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
          {CAPABILITIES.map((c, i) => {
            const Icon = c.icon;
            return (
              <Reveal key={c.t} delay={i * 0.05} className="pt-6 border-t-2 border-[var(--iv-line-strong)]">
                <span className="text-iv-green"><Icon size={22} strokeWidth={1.5} /></span>
                <div className="font-serif-display text-[24px] md:text-[28px] leading-tight text-iv-fg mt-4">{c.t}</div>
                <p className="text-[14.5px] leading-relaxed text-iv-fg-2 mt-3 max-w-md">{c.b}</p>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function CtaBand() {
  return (
    <section className="relative py-28 md:py-36 border-t border-iv-line">
      <div className="iv-container text-center">
        <div className="iv-eyebrow mb-8 justify-center flex">Start with one opportunity</div>
        <h2 className="font-serif-display text-[42px] md:text-[64px] leading-[0.98] tracking-tight text-iv-fg">
          Evaluate the evidence.<br className="hidden md:block" /> Preserve the reasoning.
        </h2>
        <div className="mt-10 flex flex-col md:flex-row items-center justify-center gap-4">
          <Link
            to={withPathway("/sample", "investor")}
            className="iv-btn iv-btn--primary text-[15px] px-8 py-4"
          >
            Try an investment decision <ArrowRight size={16} />
          </Link>
          <Link to="/companies" className="iv-btn text-[15px] px-8 py-4">
            Are you a company? See the Company pathway
          </Link>
        </div>
      </div>
    </section>
  );
}
